/**
 * CPH Wheels — Wheel Finder (Flow 1)
 * Bil-til-fælg søgning med Wheel-Size API (via proxy) + Storefront API
 */
(function () {
  'use strict';

  /* ─── CONFIG ─── */
  const PROXY_BASE = window.__cphWheels?.proxyBaseUrl || '';
  const STOREFRONT_TOKEN = window.__cphWheels?.storefrontToken || '';
  const COLLECTION_HANDLE = window.__cphWheels?.collectionHandle || 'alufaelge';
  const CENTER_BORE_TOLERANCE = 0.5;

  /* ─── ETRTO bredde-mapping (fælgbredde → dækbredde-range) ─── */
  const ETRTO_MAP = {
    5.5: [155, 185], 6.0: [175, 195], 6.5: [185, 205],
    7.0: [195, 215], 7.5: [205, 225], 8.0: [215, 235],
    8.5: [225, 245], 9.0: [235, 255], 9.5: [245, 265],
    10.0: [255, 275], 10.5: [265, 285], 11.0: [275, 295],
  };

  /* ─── STATE ─── */
  let selectedMake = null;
  let selectedModel = null;
  let selectedYear = null;
  let selectedGeneration = null; // Wheel-Size generation slug
  let carFitment = null;         // Parsed fitment data from API
  let allProducts = [];
  let currentCursor = null;
  let hasNextPage = false;

  /* ─── DOM REFS ─── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const container = $('#wheel-finder');
  if (!container) return;

  const stepIndicators = $$('[data-step-indicator]', container);
  const selectMake = $('#wf-make', container);
  const selectModel = $('#wf-model', container);
  const selectYear = $('#wf-year', container);
  const stepMake = $('#wf-step-make', container);
  const stepModel = $('#wf-step-model', container);
  const stepYear = $('#wf-step-year', container);
  const resultsSection = $('#wf-results', container);
  const resultsGrid = $('#wf-results-grid', container);
  const resultsCount = $('#wf-results-count', container);
  const carSummary = $('#wf-car-summary', container);
  const loadingSpinner = $('#wf-loading', container);
  const resetBtn = $('#wf-reset', container);
  const searchBtn = $('#wf-search-btn', container);

  /* ─── INIT ─── */
  function init() {
    populateMakes();
    restoreFromURL();
    bindEvents();

    // Bind sortering
    const sortSelect = $('#wf-sort-select', container);
    if (sortSelect) sortSelect.addEventListener('change', onSortChange);

    // Bind "Vis flere"
    const loadMoreBtn = $('#wf-load-more', container);
    if (loadMoreBtn) loadMoreBtn.addEventListener('click', onLoadMore);

    // Bind tabs og nummerplade
    initPlateSearch();
  }

  /* ─── NUMMERPLADE-SØGNING ─── */
  function initPlateSearch() {
    const plateInput = $('#wf-plate', container);
    const plateBtn = $('#wf-plate-btn', container);
    const plateInfo = $('#wf-plate-info', container);

    if (!plateInput) return;

    // Uppercase + validation while typing
    plateInput.addEventListener('input', () => {
      plateInput.value = plateInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });

    plateInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') plateBtn.click();
    });

    plateBtn.addEventListener('click', async () => {
      const plate = plateInput.value.trim();
      if (!plate || plate.length < 4) {
        plateInfo.textContent = 'Indtast venligst en gyldig nummerplade.';
        plateInfo.className = 'wf-plate-info wf-plate-info--error';
        return;
      }

      plateBtn.disabled = true;
      plateBtn.textContent = 'Søger...';
      plateInfo.textContent = 'Slår op...';
      plateInfo.className = 'wf-plate-info';

      try {
        const resp = await fetch(`${PROXY_BASE}/lookup-plate/?plate=${encodeURIComponent(plate)}`);
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.error || 'Opslag fejlede');
        }

        const data = await resp.json();

        if (!data.wheelsize || !data.wheelsize.make || !data.wheelsize.model || !data.wheelsize.year) {
          plateInfo.textContent = 'Fandt bil (' + (data.vehicle?.make || '') + ' ' + (data.vehicle?.model || '') + '), men kunne ikke matche til fælge-data. Prøv manuel søgning.';
          plateInfo.className = 'wf-plate-info wf-plate-info--error';
          return;
        }

        const displayYear = data.vehicle.year && data.vehicle.year > 0 ? data.vehicle.year : data.wheelsize.year;
        plateInfo.textContent = '✓ Fandt: ' + data.vehicle.make + ' ' + data.vehicle.model + ' (' + displayYear + ')';
        plateInfo.className = 'wf-plate-info wf-plate-info--success';

        // Redirect til samme side med URL params så restoreFromURL kører søgningen
        const params = new URLSearchParams();
        params.set('make', data.wheelsize.make);
        params.set('model', data.wheelsize.model);
        params.set('year', data.wheelsize.year);

        // Naviger - lad siden reloades så restoreFromURL + auto-søg kører
        window.location.href = window.location.pathname + '?' + params.toString();
      } catch (err) {
        console.error('Plate lookup error:', err);
        plateInfo.textContent = 'Kunne ikke finde bil med nummerplade "' + plate + '". Prøv manuel søgning.';
        plateInfo.className = 'wf-plate-info wf-plate-info--error';
      } finally {
        plateBtn.disabled = false;
        plateBtn.textContent = 'Søg';
      }
    });
  }

  function onLoadMore() {
    visibleCount += PRODUCTS_PER_PAGE;
    renderProductCards(filteredCache);
  }

  /* ─── WHEEL-SIZE API (via proxy) ─── */
  async function wsApi(path, params = {}) {
    const qs = new URLSearchParams(params).toString();
    const url = `${PROXY_BASE}${path}${qs ? '?' + qs : ''}`;
    const resp = await fetch(url);
    if (!resp.ok) {
      console.error(`API-fejl ${resp.status} for ${path}`);
      return null;
    }
    const json = await resp.json();
    // Wheel-Size API wrapper: { data: [...] } eller rent array
    return Array.isArray(json) ? json : (json.data || json);
  }

  /* ─── POPULATE DROPDOWNS ─── */
  async function populateMakes() {
    selectMake.innerHTML = '<option value="">Indlæser bilmærker...</option>';
    selectMake.disabled = true;

    const data = await wsApi('/makes/');
    if (!data) {
      selectMake.innerHTML = '<option value="">Fejl — kunne ikke hente bilmærker</option>';
      return;
    }

    selectMake.innerHTML = '<option value="">Vælg bilmærke</option>';
    (data || [])
      .sort((a, b) => a.name.localeCompare(b.name, 'da'))
      .forEach((make) => {
        const opt = document.createElement('option');
        opt.value = make.slug;
        opt.textContent = make.name;
        selectMake.appendChild(opt);
      });
    selectMake.disabled = false;
  }

  async function populateModels(makeSlug) {
    selectModel.innerHTML = '<option value="">Indlæser modeller...</option>';
    selectModel.disabled = true;

    const data = await wsApi('/models/', { make: makeSlug });
    if (!data) {
      selectModel.innerHTML = '<option value="">Fejl</option>';
      return;
    }

    selectModel.innerHTML = '<option value="">Vælg model</option>';
    (data || [])
      .sort((a, b) => a.name.localeCompare(b.name, 'da'))
      .forEach((model) => {
        const opt = document.createElement('option');
        opt.value = model.slug;
        opt.textContent = model.name;
        selectModel.appendChild(opt);
      });

    selectModel.disabled = false;
    stepModel.classList.add('wf-step--active');
  }

  async function populateYears(makeSlug, modelSlug) {
    selectYear.innerHTML = '<option value="">Indlæser årgange...</option>';
    selectYear.disabled = true;

    const data = await wsApi('/generations/', { make: makeSlug, model: modelSlug });
    if (!data) {
      selectYear.innerHTML = '<option value="">Fejl</option>';
      return;
    }

    // Wheel-Size returnerer generationer med årsintervaller
    // Vi viser individuelle årgange pr. generation
    selectYear.innerHTML = '<option value="">Vælg årgang</option>';

    const yearEntries = [];
    (data || []).forEach((gen) => {
      // API returnerer et 'years' array med tilgængelige årgange
      const years = gen.years || [];
      years.forEach((y) => {
        yearEntries.push({
          year: y,
          genSlug: gen.slug,
          genName: gen.name || '',
        });
      });
    });

    // Sorter faldende og fjern duplikater
    yearEntries
      .sort((a, b) => b.year - a.year)
      .forEach((entry) => {
        const opt = document.createElement('option');
        opt.value = `${entry.year}|${entry.genSlug}`;
        opt.textContent = entry.genName
          ? `${entry.year} (${entry.genName})`
          : `${entry.year}`;
        selectYear.appendChild(opt);
      });

    selectYear.disabled = false;
    stepYear.classList.add('wf-step--active');
  }

  /* ─── EVENT HANDLERS ─── */
  function bindEvents() {
    selectMake.addEventListener('change', onMakeChange);
    selectModel.addEventListener('change', onModelChange);
    selectYear.addEventListener('change', onYearChange);
    searchBtn?.addEventListener('click', onSearchClick);
    resetBtn?.addEventListener('click', resetFinder);
  }

  function onMakeChange() {
    selectedMake = selectMake.value;
    selectedModel = null;
    selectedYear = null;
    carFitment = null;

    selectModel.innerHTML = '<option value="">Vælg model</option>';
    selectModel.disabled = true;
    selectYear.innerHTML = '<option value="">Vælg årgang</option>';
    selectYear.disabled = true;
    stepModel.classList.remove('wf-step--active');
    stepYear.classList.remove('wf-step--active');
    hideResults();
    updateStepIndicators(1);

    if (selectedMake) {
      populateModels(selectedMake);
    }
    updateURL();
  }

  function onModelChange() {
    selectedModel = selectModel.value;
    selectedYear = null;
    carFitment = null;

    selectYear.innerHTML = '<option value="">Vælg årgang</option>';
    selectYear.disabled = true;
    stepYear.classList.remove('wf-step--active');
    hideResults();

    if (selectedModel) {
      populateYears(selectedMake, selectedModel);
      updateStepIndicators(2);
    }
    updateURL();
  }

  function onYearChange() {
    const val = selectYear.value;
    if (!val) {
      if (searchBtn) searchBtn.disabled = true;
      return;
    }

    const [year, genSlug] = val.split('|');
    selectedYear = parseInt(year);
    selectedGeneration = genSlug;

    updateStepIndicators(3);
    updateURL();

    // Auto-trigger søgning når år vælges
    if (searchBtn) searchBtn.disabled = false;
    onSearchClick();
  }

  async function onSearchClick() {
    if (!selectedMake || !selectedModel || !selectedYear) return;

    if (searchBtn) {
      searchBtn.disabled = true;
      searchBtn.textContent = 'Søger...';
    }

    // Hent fitment-data fra Wheel-Size API
    showLoading(true);
    resultsSection.hidden = false;

    const data = await wsApi('/search/by_model/', {
      make: selectedMake,
      model: selectedModel,
      year: selectedYear,
      region: 'eudm',
    });

    if (!data || !data.length) {
      showLoading(false);
      resultsGrid.innerHTML = `
        <div class="wf-no-results">
          <p>Ingen fitment-data fundet for denne bil.</p>
        </div>
      `;
      if (searchBtn) {
        searchBtn.disabled = false;
        searchBtn.textContent = 'Søg';
      }
      return;
    }

    // Parse fitment-data til vores format
    carFitment = parseFitmentData(data, selectedYear);

    // Gem til sessionStorage for Flow 2
    sessionStorage.setItem('cph_selected_car', JSON.stringify(carFitment));

    showCarSummary(carFitment);

    // Søg Shopify-produkter
    await searchProducts();

    if (searchBtn) {
      searchBtn.disabled = false;
      searchBtn.textContent = 'Søg';
    }
  }

  /**
   * Parser Wheel-Size API svar til et simpelt fitment-objekt
   * API returnerer en liste af "modifications" med wheel/tire data
   */
  function parseFitmentData(modifications, year) {
    // Specifikke godkendte bredde+ET kombinationer (deduplikeret)
    const comboSet = new Set();
    const combos = []; // [{ width, offset }]
    let boltPattern = '';
    let centerBore = 0;
    let diameterMin = Infinity;
    let diameterMax = 0;
    const tireSizes = new Set();
    let thread = '';
    let makeName = '';
    let modelName = '';

    modifications.forEach((mod) => {
      const tech = mod.technical || {};
      if (tech.bolt_pattern) boltPattern = tech.bolt_pattern;
      if (tech.centre_bore) centerBore = parseFloat(tech.centre_bore);

      if (mod.make?.name) makeName = mod.make.name;
      if (mod.generation?.name) modelName = mod.generation.name;

      if (tech.wheel_fasteners?.thread_size) {
        thread = tech.wheel_fasteners.thread_size;
      }

      const wheelSets = mod.wheels || [];
      wheelSets.forEach((ws) => {
        ['front', 'rear'].forEach((axle) => {
          const w = ws[axle];
          if (!w || !w.rim_diameter) return;

          const diameter = parseFloat(w.rim_diameter) || 0;
          const width = parseFloat(w.rim_width) || 0;
          const offset = parseFloat(w.rim_offset) || 0;

          if (diameter > 0) {
            diameterMin = Math.min(diameterMin, diameter);
            diameterMax = Math.max(diameterMax, diameter);
          }

          // Gem specifik bredde+ET kombination (dedupliker)
          if (width > 0) {
            const key = `${width}|${offset}`;
            if (!comboSet.has(key)) {
              comboSet.add(key);
              combos.push({ width, offset });
            }
          }

          if (w.tire) tireSizes.add(w.tire);
          if (w.tire_full) tireSizes.add(w.tire_full);
        });
      });
    });

    return {
      make: makeName || selectedMake,
      model: modelName || selectedModel,
      modelSlug: selectedModel,
      year: year,
      bolt_pattern: boltPattern,
      center_bore: centerBore,
      diameter_min: diameterMin === Infinity ? 15 : diameterMin,
      diameter_max: diameterMax === 0 ? 22 : diameterMax,
      combos: combos, // [{ width: 7.5, offset: 30 }, { width: 8.5, offset: 40 }, ...]
      tire_sizes: [...tireSizes],
      thread: thread,
    };
  }

  /* ─── URL MANAGEMENT ─── */
  function updateURL() {
    const params = new URLSearchParams();
    if (selectedMake) params.set('make', selectedMake);
    if (selectedModel) params.set('model', selectedModel);
    if (selectedYear) params.set('year', selectedYear);
    const newURL = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    history.replaceState(null, '', newURL);
  }

  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    const make = params.get('make');
    const model = params.get('model');
    const year = params.get('year');

    if (make) {
      // Vent på at makes er loaded, sæt derefter værdier
      const waitForMakes = setInterval(() => {
        if (selectMake.options.length > 1 && !selectMake.disabled) {
          clearInterval(waitForMakes);
          selectMake.value = make;
          selectedMake = make;

          if (model) {
            populateModels(make).then(() => {
              selectModel.value = model;
              selectedModel = model;
              stepModel.classList.add('wf-step--active');
              updateStepIndicators(2);

              if (year) {
                populateYears(make, model).then(() => {
                  // Find option der matcher årgangen
                  const yearOpt = [...selectYear.options].find((o) =>
                    o.value.startsWith(year + '|')
                  );
                  if (yearOpt) {
                    selectYear.value = yearOpt.value;
                    onYearChange();
                    // Auto-søg når vi kommer fra forside/URL
                    onSearchClick();
                  }
                });
              }
            });
          }
        }
      }, 100);

      // Stop efter 10 sek
      setTimeout(() => clearInterval(waitForMakes), 10000);
    }
  }

  /* ─── STEP INDICATORS ─── */
  function updateStepIndicators(activeStep) {
    stepIndicators.forEach((el) => {
      const step = parseInt(el.dataset.stepIndicator);
      el.classList.toggle('wf-indicator--active', step === activeStep);
      el.classList.toggle('wf-indicator--completed', step < activeStep);
    });
  }

  /* ─── CAR SUMMARY ─── */
  function showCarSummary(car) {
    if (!carSummary) return;
    const widths = [...new Set(car.combos.map((c) => c.width))].sort((a, b) => a - b);
    const widthRange = widths.length
      ? `${widths[0]}J&ndash;${widths[widths.length - 1]}J`
      : '?';

    carSummary.innerHTML = `
      <div class="wf-summary">
        <strong>${escapeHTML(car.make)} ${escapeHTML(car.model)} (${car.year})</strong>
        <span class="wf-summary__specs">
          ${escapeHTML(car.bolt_pattern)} &bull;
          CB ${car.center_bore}mm &bull;
          ${car.diameter_min}&quot;&ndash;${car.diameter_max}&quot; &bull;
          ${widthRange}
        </span>
        ${car.thread ? `<span class="wf-summary__specs">Gevind: ${escapeHTML(car.thread)}</span>` : ''}
      </div>
    `;
    carSummary.hidden = false;
  }

  /* ─── STOREFRONT API med collection filters ─── */
  function storefrontQuery(query, variables) {
    return fetch(`/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());
  }

  async function searchProducts() {
    allProducts = [];
    resultsGrid.innerHTML = '';
    showLoading(true);
    resultsSection.hidden = false;

    // Diameters: bilens range + 1" ekstra
    const diameters = [];
    for (let d = carFitment.diameter_min; d <= carFitment.diameter_max + 1; d++) {
      diameters.push(d);
    }

    // Kør ALLE diameter+krydsmål queries PARALLELT
    const queries = [];
    for (const diameter of diameters) {
      queries.push(fetchAllPages(carFitment.bolt_pattern, String(diameter)));
      queries.push(fetchAllPages('Custom', String(diameter)));
    }

    const results = await Promise.all(queries);

    // Dedupliker og saml produkter
    const seenIds = new Set();
    results.flat().forEach((p) => {
      if (!seenIds.has(p.id)) {
        seenIds.add(p.id);
        allProducts.push(p);
      }
    });

    // Klient-side filtrering: bredde, ET, centerbore, custom krydsmål
    const matched = filterProducts(allProducts, carFitment);
    showLoading(false);
    renderResults(matched);
  }

  async function fetchAllPages(boltPattern, diameter) {
    const collected = [];
    let afterCursor = null;
    let hasMore = true;

    while (hasMore) {
      const result = await fetchFilteredProducts(boltPattern, diameter, afterCursor);
      collected.push(...result.products);
      hasMore = result.hasNextPage;
      afterCursor = result.endCursor;
    }

    return collected;
  }

  async function fetchFilteredProducts(boltPattern, diameter, afterCursor) {
    const query = `
      query FilteredWheels($handle: String!, $filters: [ProductFilter!], $first: Int!, $after: String) {
        collection(handle: $handle) {
          products(first: $first, after: $after, filters: $filters) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                availableForSale
                featuredImage {
                  url
                  altText
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                metafields(identifiers: [
                  { namespace: "custom", key: "tommer" },
                  { namespace: "custom", key: "krydsmal" },
                  { namespace: "custom", key: "krydsmal_custom" },
                  { namespace: "custom", key: "nav" },
                  { namespace: "custom", key: "et" },
                  { namespace: "custom", key: "et_custom" },
                  { namespace: "custom", key: "bredde" },
                  { namespace: "custom", key: "fabrikant" },
                  { namespace: "custom", key: "model" },
                  { namespace: "my_fields", key: "leveringstid" }
                ]) {
                  namespace
                  key
                  value
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const filters = [
      { productMetafield: { namespace: "custom", key: "krydsmal", value: boltPattern } },
      { productMetafield: { namespace: "custom", key: "tommer", value: diameter } },
    ];

    try {
      const data = await storefrontQuery(query, {
        handle: COLLECTION_HANDLE,
        filters: filters,
        first: 50,
        after: afterCursor,
      });

      const collection = data?.data?.collection;
      if (!collection) return { products: [], hasNextPage: false, endCursor: null };

      const edges = collection.products.edges || [];
      const pageInfo = collection.products.pageInfo || {};

      const products = edges.map((edge) => {
        const node = edge.node;
        const mf = {};
        (node.metafields || []).forEach((m) => {
          if (m) mf[m.key] = m.value;
        });

        // Parse værdier — håndter "j" suffix, anførselstegn, "Custom" etc.
        const isCustomBP = (mf.krydsmal || '').toLowerCase() === 'custom';
        const isCustomET = (mf.et || '').toLowerCase() === 'custom';

        // Parse custom krydsmål-liste: "4x100, 5x112, 5x114,3" → array
        let customBoltPatterns = [];
        if (isCustomBP && mf.krydsmal_custom) {
          customBoltPatterns = mf.krydsmal_custom
            .split(/,\s*(?=\d)/)
            .map((s) => s.trim().toLowerCase());
        }

        // Parse custom ET range: "0 - 20" → { min: 0, max: 20 }
        let customETRange = null;
        if (isCustomET && mf.et_custom) {
          const parts = mf.et_custom.split(/\s*-\s*/);
          if (parts.length === 2) {
            customETRange = { min: parseFloat(parts[0]), max: parseFloat(parts[1]) };
          }
        }

        return {
          id: node.id,
          title: node.title,
          handle: node.handle,
          available: node.availableForSale,
          image: node.featuredImage?.url || '',
          imageAlt: node.featuredImage?.altText || node.title,
          price: node.priceRange.minVariantPrice.amount,
          currency: node.priceRange.minVariantPrice.currencyCode,
          diameter: parseFloat((mf.tommer || '').replace(/"/g, '')) || 0,
          bolt_pattern: (mf.krydsmal || '').trim(),
          bolt_pattern_custom: customBoltPatterns,
          center_bore: parseFloat(mf.nav) || 0,
          offset: parseFloat(mf.et) || 0,
          offset_custom: customETRange,
          is_custom_bp: isCustomBP,
          is_custom_et: isCustomET,
          width: parseFloat((mf.bredde || '').replace(/j/gi, '')) || 0,
          fabrikant: (mf.fabrikant || '').trim(),
          model_name: (mf.model || '').trim(),
          leveringstid: (mf.leveringstid || '').trim(),
          variants: (node.variants?.edges || []).map((v) => v.node),
        };
      });

      return {
        products,
        hasNextPage: pageInfo.hasNextPage || false,
        endCursor: pageInfo.endCursor || null,
      };
    } catch (e) {
      console.error('Storefront API fejl:', e);
      return { products: [], hasNextPage: false, endCursor: null };
    }
  }

  /* ─── MATCHING LOGIC ─── */
  const WIDTH_TOLERANCE = 0.5;  // ±0.5J
  const ET_TOLERANCE = 10;      // ±10

  function filterProducts(products, spec) {
    if (!spec) return [];

    const carBP = normalizeBP(spec.bolt_pattern);

    return products.filter((p) => {
      // 1. Krydsmål: eksakt match ELLER custom fælg der inkluderer bilens krydsmål
      if (p.is_custom_bp) {
        if (!p.bolt_pattern_custom.some((bp) => normalizeBP(bp) === carBP)) return false;
      } else {
        if (normalizeBP(p.bolt_pattern) !== carBP) return false;
      }

      // 2. Centerhul: fælgens CB >= bilens CB (navringe kan bruges for større)
      if (p.center_bore > 0 && p.center_bore < spec.center_bore - CENTER_BORE_TOLERANCE) return false;

      // 3. Diameter: bilens min – (max + 1"). Uafhængigt af bredde/ET.
      if (p.diameter < spec.diameter_min || p.diameter > spec.diameter_max + 1) return false;

      // 4. Bredde + ET: skal matche MINDST ÉN godkendt kombination (±0.5J bredde, ±10 ET)
      if (!matchesAnyCombo(p, spec.combos)) return false;

      return true;
    });
  }

  /**
   * Tjekker om en fælg matcher mindst én godkendt bredde+ET kombination.
   * Custom ET-fælge: tjek om fælgens ET-range overlapper med combo ET ±tolerance.
   */
  function matchesAnyCombo(product, combos) {
    if (!combos || !combos.length) return true; // Ingen combo-data = vis alt

    for (const combo of combos) {
      // Bredde: ±0.5J
      if (Math.abs(product.width - combo.width) > WIDTH_TOLERANCE) continue;

      // ET: ±10
      if (product.is_custom_et && product.offset_custom) {
        // Custom ET fælg: tjek overlap mellem fælgens range og combo ET ±10
        const comboETMin = combo.offset - ET_TOLERANCE;
        const comboETMax = combo.offset + ET_TOLERANCE;
        if (product.offset_custom.max >= comboETMin && product.offset_custom.min <= comboETMax) {
          return true;
        }
      } else {
        // Normal fælg: eksakt ET inden for ±10
        if (Math.abs(product.offset - combo.offset) <= ET_TOLERANCE) {
          return true;
        }
      }
    }

    return false;
  }

  function normalizeBP(bp) {
    return (bp || '').replace(/\s/g, '').toLowerCase();
  }

  /* ─── FILTER + SORT STATE ─── */
  let matchedProducts = [];
  let activeFilters = {};
  let activeSort = 'relevance';
  const PRODUCTS_PER_PAGE = 50;
  let visibleCount = PRODUCTS_PER_PAGE;

  /* ─── RENDER RESULTS med filtre og sortering ─── */
  function renderResults(products) {
    matchedProducts = products;
    activeFilters = {};
    activeSort = 'relevance';
    visibleCount = PRODUCTS_PER_PAGE;

    if (products.length === 0) {
      resultsGrid.innerHTML = `
        <li class="wf-no-results">
          <p>Ingen fælge fundet til din bil.</p>
          <p>Prøv at ændre din søgning eller kontakt os for hjælp.</p>
        </li>
      `;
      resultsCount.textContent = '0 fælge fundet';
      const sidebar = $('#wf-filters', container);
      if (sidebar) sidebar.hidden = true;
      const loadMoreBtn = $('#wf-load-more', container);
      if (loadMoreBtn) loadMoreBtn.hidden = true;
      return;
    }

    buildFilterSidebar();
    applyFiltersAndSort();
  }

  const filterDefs = [
    { key: 'fabrikant', label: 'Mærke', getter: (p) => p.fabrikant || 'Ukendt' },
    { key: 'model_name', label: 'Model', getter: (p) => p.model_name || 'Ukendt' },
    { key: 'diameter', label: 'Diameter', getter: (p) => p.diameter, sort: 'num' },
    { key: 'width', label: 'Bredde', getter: (p) => p.width + 'J', sort: 'num' },
    { key: 'offset_display', label: 'Indpresning (ET)', getter: (p) => p.is_custom_et ? 'Custom' : String(p.offset), sort: 'num' },
  ];

  const CARET_SVG = '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path></svg>';
  const CHECK_SVG = '<div class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-checkmark" viewBox="0 0 12 9"><path fill="currentColor" fill-rule="evenodd" d="M11.35.643a.5.5 0 0 1 .006.707l-6.77 6.886a.5.5 0 0 1-.719-.006L.638 4.845a.5.5 0 1 1 .724-.69l2.872 3.011 6.41-6.517a.5.5 0 0 1 .707-.006z" clip-rule="evenodd"></path></svg></div>';
  const CHECKBOX_SVG = '<svg width="16px" height="16px" viewBox="0 0 16 16"><rect width="16" height="16" fill="none" stroke="currentColor"></rect></svg>';

  function buildFilterSidebar() {
    const sidebar = $('#wf-filters', container);
    if (!sidebar) return;
    sidebar.hidden = false;

    let html = '<div class="facets facets-vertical"><h2 class="facet-filters__label caption-large text-body" style="margin-bottom:1rem;">Filter:</h2>';

    filterDefs.forEach((def, idx) => {
      // Find tilgængelige værdier MED andre aktive filtre (men uden dette)
      const otherFilters = { ...activeFilters };
      delete otherFilters[def.key];
      const available = applyFilterSet(matchedProducts, otherFilters);

      const counts = new Map();
      available.forEach((p) => {
        const raw = def.getter(p);
        if (raw == null || raw === '' || raw === 'Ukendt') return;
        const v = String(raw);
        counts.set(v, (counts.get(v) || 0) + 1);
      });

      let entries = [...counts.entries()];
      if (entries.length === 0) return;

      if (def.sort === 'num') {
        entries.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
      } else {
        entries.sort((a, b) => a[0].localeCompare(b[0], 'da'));
      }

      const selectedCount = activeFilters[def.key]?.length || 0;
      const isOpen = selectedCount > 0 ? ' open' : '';

      const items = entries.map(([val, count], i) => {
        const checked = activeFilters[def.key]?.includes(val) ? ' checked' : '';
        const safeVal = escapeHTML(val);
        const id = `wf-filter-${def.key}-${i}`;
        return `<li class="list-menu__item facets__item">
          <label for="${id}" class="facets__label facet-checkbox">
            <input type="checkbox" data-filter-key="${def.key}" value="${safeVal}" id="${id}"${checked}>
            ${CHECKBOX_SVG}
            ${CHECK_SVG}
            <span class="facet-checkbox__text" aria-hidden="true">
              <span class="facet-checkbox__text-label">${safeVal}</span> (${count})
            </span>
          </label>
        </li>`;
      }).join('');

      html += `
        <details class="facets__disclosure-vertical js-filter" data-index="${idx + 1}"${isOpen}>
          <summary class="facets__summary caption-large focus-offset">
            <div>
              <span class="facets__summary-label">${escapeHTML(def.label)}
                <span class="facets__selected${selectedCount === 0 ? ' hidden' : ''}">(${selectedCount})</span>
              </span>
              ${CARET_SVG}
            </div>
          </summary>
          <div class="parent-display facets__display-vertical">
            <fieldset class="facets-wrap parent-wrap facets-wrap-vertical">
              <legend class="visually-hidden">${escapeHTML(def.label)}</legend>
              <ul class="facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled" role="list">
                ${items}
              </ul>
            </fieldset>
          </div>
        </details>
      `;
    });

    html += '</div>';
    sidebar.innerHTML = html;

    $$('input[data-filter-key]', sidebar).forEach((input) => {
      input.addEventListener('change', onFilterChange);
    });
  }

  /** Anvend et sæt filtre på produkter (bruges til at beregne tilgængelige filterværdier) */
  function applyFilterSet(products, filters) {
    let filtered = products;
    Object.entries(filters).forEach(([key, values]) => {
      if (!values || !values.length) return;
      filtered = filtered.filter((p) => {
        const def = filterDefs.find((d) => d.key === key);
        if (!def) return true;
        return values.includes(String(def.getter(p)));
      });
    });
    return filtered;
  }

  function onFilterChange() {
    const sidebar = $('#wf-filters', container);
    activeFilters = {};

    $$('input[data-filter-key]:checked', sidebar).forEach((input) => {
      const key = input.getAttribute('data-filter-key');
      if (!activeFilters[key]) activeFilters[key] = [];
      activeFilters[key].push(input.value);
    });

    applyFiltersAndSort();
    buildFilterSidebar();
  }

  function onSortChange(e) {
    activeSort = e.target.value;
    applyFiltersAndSort();
  }

  function applyFiltersAndSort() {
    let filtered = applyFilterSet(matchedProducts, activeFilters);

    // Sorter
    switch (activeSort) {
      case 'price-asc':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-desc':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'title-asc':
        filtered.sort((a, b) => a.title.localeCompare(b.title, 'da'));
        break;
      case 'title-desc':
        filtered.sort((a, b) => b.title.localeCompare(a.title, 'da'));
        break;
      default:
        // Relevans: Japan Racing først, derefter pris lav→høj
        filtered.sort((a, b) => {
          const aJR = a.fabrikant.toLowerCase().includes('japan racing') ? 0 : 1;
          const bJR = b.fabrikant.toLowerCase().includes('japan racing') ? 0 : 1;
          if (aJR !== bJR) return aJR - bJR;
          return parseFloat(a.price) - parseFloat(b.price);
        });
        break;
    }

    resultsCount.textContent = `${filtered.length} fælge fundet`;
    visibleCount = PRODUCTS_PER_PAGE;
    renderProductCards(filtered);
  }

  let filteredCache = [];

  function renderProductCards(products) {
    filteredCache = products;
    resultsGrid.innerHTML = '';

    const loadMoreBtn = $('#wf-load-more', container);

    if (products.length === 0) {
      resultsGrid.innerHTML = `
        <li class="wf-no-results">
          <p>Ingen fælge matcher dine filtre.</p>
        </li>
      `;
      if (loadMoreBtn) loadMoreBtn.hidden = true;
      return;
    }

    const toShow = products.slice(0, visibleCount);

    // Vis/skjul "Vis flere" knap
    if (loadMoreBtn) {
      loadMoreBtn.hidden = visibleCount >= products.length;
      loadMoreBtn.textContent = `Vis flere (${Math.min(PRODUCTS_PER_PAGE, products.length - visibleCount)} mere)`;
    }

    toShow.forEach((p) => {
      const priceFormatted = formatPrice(p.price, p.currency);
      const imgBase = p.image || '';
      const sep = imgBase.includes('?') ? '&' : '?';

      const li = document.createElement('li');
      li.className = 'grid__item';

      li.innerHTML = `
        <div class="card-wrapper product-card-wrapper underline-links-hover product-card"
          data-bolt-pattern="${escapeHTML(p.bolt_pattern)}"
          data-rim-diameter="${p.diameter}"
          data-rim-width="${p.width}"
          data-rim-offset="${p.offset}"
          data-nav="${p.center_bore}">
          <div class="card card--card card--media color-scheme-2 gradient" style="--ratio-percent: 100%;">
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              ${imgBase ? `
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <img
                    srcset="${imgBase}${sep}width=165 165w,${imgBase}${sep}width=360 360w,${imgBase}${sep}width=533 533w,${imgBase}${sep}width=720 720w,${imgBase}${sep}width=940 940w,${imgBase}${sep}width=1066 1066w"
                    src="${imgBase}${sep}width=533"
                    sizes="(min-width: 1200px) 267px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                    alt="${escapeHTML(p.imageAlt)}"
                    class="motion-reduce"
                    loading="lazy"
                    width="1080"
                    height="1080"
                  >
                </div>
              </div>
              ` : ''}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="/products/${p.handle}" class="full-unstyled-link">
                      ${escapeHTML(p.title)}
                    </a>
                  </h3>
                </div>
                <div class="card__badge bottom left">
                  ${!p.available ? '<span class="badge badge--bottom-left color-3">Udsolgt</span>' : ''}
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="/products/${p.handle}" class="full-unstyled-link">
                    ${escapeHTML(p.title)}
                  </a>
                </h3>
                <div class="card-information">
                  <div style="margin-top: 0.5rem;" class="caption-with-letter-spacing light">
                    Krydsmål: ${escapeHTML(p.bolt_pattern)}<br>
                    Diameter: ${p.diameter}<br>
                    Bredde: ${p.width}J<br>
                    Indpresning (ET): ${p.is_custom_et ? 'Custom (' + p.offset_custom.min + '-' + p.offset_custom.max + ')' : p.offset}<br>
                    Centerhul: ${p.center_bore}
                  </div>
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">Normalpris</span>
                        <span class="price-item price-item--regular">${priceFormatted}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card__badge bottom left">
                ${!p.available ? '<span class="badge badge--bottom-left color-3">Udsolgt</span>' : ''}
              </div>
            </div>
          </div>
        </div>
      `;

      resultsGrid.appendChild(li);
    });
  }

  /* ─── RESET ─── */
  function resetFinder() {
    selectedMake = null;
    selectedModel = null;
    selectedYear = null;
    selectedGeneration = null;
    carFitment = null;

    selectMake.value = '';
    selectModel.innerHTML = '<option value="">Vælg model</option>';
    selectModel.disabled = true;
    selectYear.innerHTML = '<option value="">Vælg årgang</option>';
    selectYear.disabled = true;

    stepModel.classList.remove('wf-step--active');
    stepYear.classList.remove('wf-step--active');
    updateStepIndicators(0);
    hideResults();
    updateURL();
    sessionStorage.removeItem('cph_selected_car');
  }

  /* ─── HELPERS ─── */
  function showLoading(show) {
    if (loadingSpinner) {
      loadingSpinner.hidden = !show;
      loadingSpinner.style.display = show ? '' : 'none';
    }
  }

  function hideResults() {
    resultsSection.hidden = true;
    resultsGrid.innerHTML = '';
    if (carSummary) carSummary.hidden = true;
  }

  function formatPrice(amount, currency) {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency || 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  }

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ─── PUBLIC API ─── */
  window.cphWheelFinder = {
    getSelectedCar: () => carFitment,
    getEtrtoRange: (width) => ETRTO_MAP[width] || null,
    reset: resetFinder,
  };

  /* ─── GO ─── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
